import React from 'react'
import { observer } from 'mobx-react'
import ScopeChangeRequestDetailsViewModel from '../ScopeChangeRequestDetailsViewModel'
import { formatMoney } from 'utils/money-util'
import {
  formatDate,
  formatStatus,
  formatStatusSingleWord,
  formatSum,
  formatSidePanelStatus,
} from 'admin/scope-change-requests/formatters/scope-change-request-formatters'
import styles from './InfoSection.sass'
import cx from 'classnames'

interface InfoSectionItemProps {
  label: string
  children: React.ReactNode
  valuebottomSpacing?: boolean
}

const InfoSectionItem = observer(function InfoSectionItem({
  label,
  children,
  valuebottomSpacing = false,
}: InfoSectionItemProps) {
  return (
    <>
      <div className={styles.infoItemLabel}>{label}</div>
      <div
        className={cx(
          styles.infoItemValue,
          valuebottomSpacing && styles.valuebottomSpacing,
        )}
      >
        {children}
      </div>
    </>
  )
})

interface InfoSectionItemsProps {
  children: React.ReactNode
}

const InfoSectionItems = observer(function InfoSectionItems({
  children,
}: InfoSectionItemsProps) {
  return <div className={styles.infoItems}>{children}</div>
})

interface InfoSectionProps {
  vm: ScopeChangeRequestDetailsViewModel
}

export default observer(function InfoSection({ vm }: InfoSectionProps) {
  return (
    <div className={styles.info}>
      <h2 className={styles.infoTitle}>
        {vm.scopeChangeRequest.job.displayName}
      </h2>
      <InfoSectionItems>
        <InfoSectionItem label="Job ID">
          {vm.scopeChangeRequest.job.shortId}
        </InfoSectionItem>
        <InfoSectionItem label="Legend">
          {vm.scopeChangeRequest.job.legendName}
        </InfoSectionItem>
        <InfoSectionItem label="Workspace">
          {vm.scopeChangeRequest.workspace.name}
        </InfoSectionItem>
        <InfoSectionItem label="Requested by">
          {vm.scopeChangeRequest.createdBy.displayName}
        </InfoSectionItem>
        <InfoSectionItem label="Date">
          {formatDate(vm.scopeChangeRequest.createTime)}{' '}
          <span className={styles.infoTimeAgo}>
            ({vm.scopeChangeRequest.open})
          </span>
        </InfoSectionItem>
        {vm.jobBundle && (
          <InfoSectionItem label="Bundle">{vm.bundleInfo}</InfoSectionItem>
        )}
        <InfoSectionItem
          label={
            vm.scopeChangeRequest.isRejected ? 'Rejected total' : 'New total'
          }
        >
          {formatMoney(
            vm.scopeChangeRequestPriceBreakDown.previousTotalPrice || 0,
          )}{' '}
          <span
            className={
              vm.scopeChangeRequestPriceBreakDown.totalPriceDiff >= 0
                ? styles.totalPlus
                : styles.totalMinus
            }
          >
            {formatSum(vm.scopeChangeRequestPriceBreakDown.totalPriceDiff)}
          </span>{' '}
          ={' '}
          {formatMoney(vm.scopeChangeRequestPriceBreakDown.newTotalPrice || 0)}
        </InfoSectionItem>
        <InfoSectionItem
          label={
            vm.scopeChangeRequest.isRejected
              ? 'Rejected pro cut'
              : 'New pro cut'
          }
        >
          {formatMoney(
            vm.scopeChangeRequestPriceBreakDown.previousTotalProPrice || 0,
          )}{' '}
          <span
            className={
              vm.scopeChangeRequestPriceBreakDown.totalProPriceDiff >= 0
                ? styles.totalPlus
                : styles.totalMinus
            }
          >
            {formatSum(vm.scopeChangeRequestPriceBreakDown.totalProPriceDiff)}
          </span>{' '}
          ={' '}
          {formatMoney(
            vm.scopeChangeRequestPriceBreakDown.newTotalProPrice || 0,
          )}
        </InfoSectionItem>
        {vm.percentageChangeApplied && (
          <InfoSectionItem label="Notes">{vm.percentageNote}</InfoSectionItem>
        )}
        <InfoSectionItem label="Status">
          <span
            data-test={`scope-change-request-status-${formatStatus(
              vm.scopeChangeRequest,
            )}`}
            className={
              styles[
                `status-${formatStatusSingleWord(vm.scopeChangeRequest.status)}`
              ]
            }
          >
            {formatSidePanelStatus(vm.scopeChangeRequest)}
          </span>
        </InfoSectionItem>
      </InfoSectionItems>
    </div>
  )
})

export { InfoSectionItem, InfoSectionItems }
